import React, { useEffect, useRef, useState } from "react";
import { Lightbulb, LightbulbOff } from "lucide-react";
import { useLanguage } from "../i18n";

const KEY = "farlight-light";
const LEVELS = [
  { id: "day", pct: "100%" },
  { id: "night", pct: "10%" },
];
const LABELS = {
  it: { title: "Dimmer luce", day: "Giorno", night: "Notte" },
  en: { title: "Light dimmer", day: "Day", night: "Night" },
  fr: { title: "Variateur de lumière", day: "Jour", night: "Nuit" },
  de: { title: "Lichtdimmer", day: "Tag", night: "Nacht" },
  es: { title: "Regulador de luz", day: "Día", night: "Noche" },
};

const readLevel = () => {
  try {
    const v = localStorage.getItem(KEY);
    return LEVELS.some((l) => l.id === v) ? v : "day";
  } catch {
    return "day";
  }
};

// Applies the lighting mood to <html data-light>; the colors live in index.css (--mood-*).
// Pages without the Header (admin, login) always render in Day.
const useLightLevel = () => {
  const [level, setLevelState] = useState(readLevel);
  const fadeTimer = useRef(0);

  useEffect(() => {
    const root = document.documentElement;
    if (level === "day") delete root.dataset.light;
    else root.dataset.light = level;
    return () => {
      delete root.dataset.light;
    };
  }, [level]);

  const setLevel = (next) => {
    if (next === level) return;
    const root = document.documentElement;
    root.classList.add("light-switching");
    window.clearTimeout(fadeTimer.current);
    fadeTimer.current = window.setTimeout(() => root.classList.remove("light-switching"), 1000);
    try {
      localStorage.setItem(KEY, next);
    } catch {}
    setLevelState(next);
  };

  return [level, setLevel];
};

export const LightDimmer = () => {
  const { lang } = useLanguage();
  const L = LABELS[lang] || LABELS.en;
  const [level, setLevel] = useLightLevel();
  const idx = LEVELS.findIndex((l) => l.id === level);
  const current = LEVELS[idx];
  const next = LEVELS[(idx + 1) % LEVELS.length];

  const on = level === "day";

  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      data-testid="light-dimmer-toggle"
      onClick={() => setLevel(next.id)}
      aria-label={`${L.title}: ${L[current.id]} ${current.pct}`}
      title={`${L.title}: ${L[current.id]} ${current.pct}`}
      className={`relative shrink-0 w-[46px] h-6 rounded-full border transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amberl ${on ? "bg-amberl/35 border-amberl" : "bg-ink/10 border-ink/25"}`}
    >
      <span
        aria-hidden="true"
        className={`absolute top-[1px] left-[1px] flex items-center justify-center w-5 h-5 rounded-full transition-all duration-300 ${on ? "translate-x-[22px] bg-amberl text-espresso shadow-[0_0_12px_rgba(212,163,115,0.85)]" : "translate-x-0 bg-inksoft text-cream"}`}
      >
        {on ? <Lightbulb className="w-3 h-3" strokeWidth={2} /> : <LightbulbOff className="w-3 h-3" strokeWidth={2} />}
      </span>
    </button>
  );
};

// A small bulb lights up where the visitor clicks or taps; the pointer itself stays the standard one.
// Mouse and pen: on press. Touch: on release, so a swipe to scroll (which ends in pointercancel) shows nothing.
export const ClickBulb = () => {
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: no-preference)");
    if (!mq.matches) return undefined;
    const show = (e) => {
      const el = document.createElement("span");
      el.className = "click-bulb";
      el.setAttribute("aria-hidden", "true");
      el.style.left = `${e.clientX}px`;
      el.style.top = `${e.clientY}px`;
      el.addEventListener("animationend", () => el.remove());
      document.body.appendChild(el);
    };
    const onDown = (e) => {
      if (e.pointerType === "touch" || e.button !== 0) return;
      show(e);
    };
    const onUp = (e) => {
      if (e.pointerType === "touch") show(e);
    };
    window.addEventListener("pointerdown", onDown, { passive: true });
    window.addEventListener("pointerup", onUp, { passive: true });
    return () => {
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);
  return null;
};
